<template>
  <div class="course-detail-page">
    <!-- 課程詳情 Banner 區域 -->
    <LightBlueBanner :exposedHeight="260">
      <div
        v-if="course.title"
        :class="['detail-status-tag', getCourseStatus(course)]"
      >
        {{ getStatusLabel(getCourseStatus(course), true) }}
      </div>
      <h1 class="course-main-title" v-if="course.title">
        {{ displayLoc(course, "title") }}
      </h1>

      <!-- 操作按鈕列 -->
      <div class="actions-bar">
        <a-button
          v-if="course.external_url"
          type="primary"
          class="enroll-button"
          @click="handleEnroll"
        >
          {{ getActionButtonText(getCourseStatus(course)) }}
        </a-button>

        <!-- 收藏按鈕 -->
        <a-button
          :class="['bookmark-button', { 'is-favorited': isFavorite }]"
          @click="toggleFavorite"
          :loading="favoriteLoading"
        >
          <template #icon>
            <div class="bookmark-icon-container">
              <svg
                width="14"
                height="18"
                viewBox="0 0 14 18"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                class="bookmark-icon"
              >
                <path
                  d="M0 18V2C0 1.45 0.196 0.979333 0.588 0.588C0.98 0.196667 1.45067 0.000666667 2 0H12C12.55 0 13.021 0.196 13.413 0.588C13.805 0.98 14.0007 1.45067 14 2V18L7 15L0 18Z"
                  :fill="bookmarkPathFill"
                  stroke="#1171DD"
                  stroke-width="1.5"
                />
              </svg>
            </div>
          </template>
          {{
            isFavorite
              ? t("courseDetail.favorited")
              : t("courseDetail.favorite")
          }}
        </a-button>
      </div>
    </LightBlueBanner>

    <Breadcrumb :items="breadcrumbItems" />

    <!-- 固定導航欄 -->
    <div
      class="sticky-nav"
      :class="{
        show: uiState.showStickyNav,
        'navbar-visible': uiState.showNavbar,
        'navbar-hidden': !uiState.showNavbar,
      }"
    >
      <div class="sticky-nav-container">
        <div class="sticky-nav-content">
          <div class="sticky-nav-left">
            <h3 class="sticky-course-title">
              {{ displayLoc(course, "title") }}
            </h3>
          </div>

          <div class="sticky-nav-right">
            <a-button
              v-if="course.external_url"
              type="primary"
              class="sticky-cta-button"
              @click="handleEnroll"
            >
              {{ getActionButtonText(getCourseStatus(course)) }}
            </a-button>
          </div>
        </div>

        <hr class="sticky-nav-divider" />

        <div class="sticky-nav-tabs">
          <a
            v-for="item in navItems"
            :key="item.id"
            :href="'#' + item.id"
            class="sticky-nav-tab"
            :class="{ active: activeSection === item.id }"
            @click.prevent="scrollToSection(item.id)"
            v-show="!item.condition || item.condition()"
          >
            <span class="desktop-text">{{ item.label }}</span>
            <span class="mobile-text">{{ item.mobileLabel }}</span>
          </a>
        </div>
      </div>
    </div>

    <!-- 主要內容區域 -->
    <div class="content-wrapper" ref="contentWrapperRef">
      <!-- 滾動導航欄 -->
      <div class="content-nav-wrapper">
        <div class="content-nav">
          <a
            v-for="item in navItems"
            :key="item.id"
            :href="'#' + item.id"
            class="nav-item"
            :class="{ active: activeSection === item.id }"
            @click.prevent="scrollToSection(item.id)"
            v-show="!item.condition || item.condition()"
          >
            <span class="desktop-text">{{ item.label }}</span>
            <span class="mobile-text">{{ item.mobileLabel }}</span>
          </a>
        </div>
      </div>

      <!-- 內容區塊 -->
      <div class="content-sections" @click="handleBodyClick">
        <!-- 時間地點 -->
        <div
          id="time-location"
          class="content-section"
          ref="timeLocationRef"
          v-if="course.class_start_at || course.class_location"
        >
          <h2 class="section-title">{{ t("courseDetail.timeLocation") }}</h2>
          <div class="content-card time-location-card">
            <div
              class="time-row"
              v-for="(timeLine, index) in formattedClassTimes"
              :key="index"
            >
              {{ timeLine }}
            </div>
            <div class="location-row" v-if="course.class_location">
              {{ course.class_location }}
            </div>
          </div>
        </div>

        <!-- 課程介紹 -->
        <div id="course-intro" class="content-section" ref="courseIntroRef">
          <h2 class="section-title">{{ t("courseDetail.courseIntro") }}</h2>
          <div class="content-card">
            <div
              class="course-description"
              v-html="displayLoc(course, 'description')"
            ></div>
          </div>
        </div>

        <!-- 工作坊議程 -->
        <div
          id="workshop-agenda"
          class="content-section"
          ref="workshopAgendaRef"
          v-if="hasAgenda"
        >
          <h2 class="section-title">{{ t("courseDetail.workshopAgenda") }}</h2>
          <div class="content-card">
            <div
              class="course-description"
              v-html="displayLoc(course, 'agenda')"
            ></div>
          </div>
        </div>

        <!-- 單元一覽 -->
        <div
          id="chapter-list"
          class="content-section"
          ref="chapterListRef"
          v-if="hasChapters"
        >
          <h2 class="section-title">{{ t("courseDetail.chapterList") }}</h2>
          <a-collapse
            v-model:activeKey="activeChapters"
            :bordered="false"
            class="chapters-collapse"
          >
            <a-collapse-panel
              v-for="chapter in course.chapters"
              :key="chapter.id"
              :header="displayLoc(chapter, 'title')"
              class="chapter-panel"
            >
              <div class="sub-chapter-item">
                <div role="button" class="sub-chapter-icon">
                  <PlayCircleOutlined />
                </div>
                <div class="sub-chapter-content">
                  <div class="sub-chapter-title">
                    {{ displayLoc(chapter, "title") }}
                  </div>
                  <div class="sub-chapter-duration">
                    {{ formatDuration(chapter) }}
                  </div>
                </div>
              </div>
            </a-collapse-panel>
          </a-collapse>
        </div>

        <!-- 講師資料 -->
        <div
          id="instructor-info"
          class="content-section"
          ref="instructorInfoRef"
        >
          <h2 class="section-title">{{ t("courseDetail.aboutInstructor") }}</h2>
          <div class="instructors-info">
            <div
              v-for="instructor in course.instructors"
              :key="instructor.id"
              class="instructor-card"
            >
              <div class="instructor-photo">
                <img
                  :src="getInstructorPhoto(instructor)"
                  alt="instructor photo"
                  @error="handleImageError"
                />
              </div>
              <div class="instructor-content">
                <div class="instructor-header">
                  <h3 class="instructor-name">
                    {{ displayLoc(instructor, "name") }}
                  </h3>
                  <div class="instructor-position">
                    {{ displayLoc(instructor, "position") }}
                  </div>
                </div>
                <p class="instructor-description">
                  {{ displayLoc(instructor, "description") }}
                </p>
              </div>
            </div>
            <div v-if="!course.instructors?.length" class="no-instructors">
              {{ t("courseDetail.noInstructorData") }}
            </div>
          </div>
        </div>

        <!-- 獲得證書 -->
        <div
          id="faq-section"
          class="content-section"
          ref="faqSectionRef"
          v-if="course.has_certificate"
        >
          <h2 class="section-title">{{ t("courseDetail.getCertificate") }}</h2>
          <div class="content-card">
            <div class="certificate-info">
              <h4 class="certificate-subtitle">
                {{ t("courseDetail.youWillGetCertificate") }}
              </h4>
              <div class="certificate-steps">
                <div class="certificate-step">
                  <div class="step-content">
                    <div class="certificate-benefit">
                      <CheckCircleTwoTone
                        :twoToneColor="['#ffffff', '#022964']"
                        class="benefit-icon"
                      />
                      <span>{{ t("courseDetail.formalLearningRecord") }}</span>
                    </div>
                    <div class="certificate-benefit">
                      <CheckCircleTwoTone
                        :twoToneColor="['#ffffff', '#022964']"
                        class="benefit-icon"
                      />
                      <span>{{ t("courseDetail.enhanceResume") }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- Lightbox -->
      <ImageLightbox
        :visible="showLightbox"
        :src="currentLightboxImage"
        :alt="displayLoc(course, 'title')"
        @close="closeLightbox"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onUnmounted, nextTick } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { message } from "ant-design-vue";
import { msCourseAPI } from "@/services/api.js";
// import { courseAPI } from "../services/api";
import Breadcrumb from "./Breadcrumb.vue";
import LightBlueBanner from "./LightBlueBanner.vue";

const { t, locale } = useI18n();
const route = useRoute();
const router = useRouter();

// === 狀態管理 ===
const courseId = computed(() => route.params.id || "");
const course = ref({
  title: "",
  subtitle: "",
  description: "",
  agenda: "",
  en_agenda: "",
  credit: 0,
  tags: [],
  categories: [],
  instructors: [],
  chapters: [],
  level: "",
  language: "",
  total_time: "",
  has_certificate: false,
  has_exam: false,
  needs_registration: 1,
  cmio: 0,
  user_tags: [],
  class_location: "",
  registration_start_at: "",
  registration_end_at: "",
  class_start_at: "",
  class_end_at: "",
  status_override: null,
});

const isFavorite = ref(false);
const favoriteLoading = ref(false);
const activeSection = ref("time-location");
const activeChapters = ref([]);
const bookmarkPathFill = computed(() =>
  isFavorite.value ? "#1171DD" : "none"
);
const hasAgenda = computed(
  () => displayLoc(course.value, "agenda").trim().length > 0
);
const hasChapters = computed(
  () => Array.isArray(course.value.chapters) && course.value.chapters.length > 0
);

import { useLightbox } from "@/composables/useLightbox";
import ImageLightbox from "@/components/ImageLightbox.vue";

const { showLightbox, currentLightboxImage, closeLightbox, handleBodyClick } =
  useLightbox();

// UI 相關狀態
const uiState = reactive({
  showStickyNav: false,
  showNavbar: true,
  loading: true,
});

// Refs for Scroll
const contentWrapperRef = ref(null);
const timeLocationRef = ref(null);
const courseIntroRef = ref(null);
const workshopAgendaRef = ref(null);
const chapterListRef = ref(null);
const instructorInfoRef = ref(null);
const faqSectionRef = ref(null);

// === Helpers ===
const getCourseStatus = (course) => {
  if (course.status_override) {
    return course.status_override;
  }
  const now = new Date();
  const regStart = course.registration_start_at
    ? new Date(course.registration_start_at.replace(/-/g, "/"))
    : null;
  const regEnd = course.registration_end_at
    ? new Date(course.registration_end_at.replace(/-/g, "/"))
    : null;
  const classEnd = course.class_end_at
    ? new Date(course.class_end_at.replace(/-/g, "/"))
    : null;

  if (!regStart || !regEnd || !classEnd) {
    return "not_started";
  }

  if (now < regStart) {
    return "not_started";
  } else if (now >= regStart && now <= regEnd) {
    return "open";
  } else if (now > regEnd && now < classEnd) {
    return "closed";
  } else {
    return "ended";
  }
};

const getStatusLabel = (status, isDetail) => {
  if (isDetail) {
    switch (status) {
      case "not_started":
        return "尚未開放報名";
      case "open":
        return "報名中";
      case "closed":
        return "報名已截止";
      case "ended":
        return "課程已結束";
      default:
        return "";
    }
  } else {
    switch (status) {
      case "not_started":
        return "即將開放";
      case "open":
        return "報名中";
      case "closed":
        return "報名截止";
      case "ended":
        return "已結束";
      default:
        return "";
    }
  }
};

const getActionButtonText = (status) => {
  return status === "open" ? "前往報名" : "查看課程資訊";
};

const parseImageCaptions = (html) => {
  if (!html) return "";
  return html.replace(/<img([^>]+)>/gi, (imgTag, attrs) => {
    const altMatch = attrs.match(/alt=["']([^"']*)["']/i);
    const altText = altMatch ? altMatch[1].trim() : "";
    if (altText) {
      return `<figure class="rich-figure"><img${attrs}><figcaption class="image-caption">${altText}</figcaption></figure>`;
    }
    return imgTag;
  });
};

const displayLoc = (obj, key) => {
  if (!obj) return "";
  let val = "";
  if (locale.value === "en-US") {
    val = obj[`en_${key}`] || obj[key] || "";
  } else {
    val = obj[key] || "";
  }
  if (typeof val === "string" && (key === "description" || key === "agenda" || key === "content")) {
    return parseImageCaptions(val);
  }
  return val;
};

const formattedClassTimes = computed(() => {
  const startStr = course.value.class_start_at;
  const endStr = course.value.class_end_at;
  if (!startStr) return [];

  const startParts = startStr.split(" ");
  if (startParts.length < 2) return [startStr];

  const startDate = startParts[0];
  const startTime = startParts[1].substring(0, 5); // HH:mm

  if (!endStr) {
    return [`${startDate} | ${startTime}`];
  }

  const endParts = endStr.split(" ");
  if (endParts.length < 2) {
    return [`${startDate} | ${startTime}`];
  }

  const endDate = endParts[0];
  const endTime = endParts[1].substring(0, 5); // HH:mm

  if (startDate === endDate) {
    return [`${startDate} | ${startTime} - ${endTime}`];
  } else {
    return [
      `${startDate} | ${startTime} - ${endTime}`,
      `${endDate} | ${startTime} - ${endTime}`,
    ];
  }
});

const formatDuration = (chapter) => {
  const duration =
    course.value.vimeo?.[chapter.id]?.duration || chapter.duration;
  if (!duration) return "未指定時長";
  const sec = parseInt(duration);
  if (isNaN(sec)) return "未指定時長";
  return `${Math.floor(sec / 60)
    .toString()
    .padStart(2, "0")}:${(sec % 60).toString().padStart(2, "0")}`;
};

const breadcrumbItems = computed(() => [
  { label: "首頁", path: "/" },
  { label: "認證培訓" },
  { label: "工作坊課程" },
  { label: displayLoc(course.value, "title") },
]);

const navItems = computed(() => [
  {
    id: "time-location",
    label: t("courseDetail.timeLocation"),
    mobileLabel: t("courseDetail.timeLocation"),
    condition: () =>
      !!(course.value.class_start_at || course.value.class_location),
  },
  {
    id: "course-intro",
    label: t("courseDetail.courseIntro"),
    mobileLabel: t("courseDetail.courseIntro"),
  },
  {
    id: "workshop-agenda",
    label: t("courseDetail.workshopAgenda"),
    mobileLabel: t("courseDetail.workshopAgenda"),
    condition: () => hasAgenda.value,
  },
  {
    id: "chapter-list",
    label: t("courseDetail.chapterList"),
    mobileLabel: t("courseDetail.chapterList"),
    condition: () => hasChapters.value,
  },
  {
    id: "instructor-info",
    label: t("courseDetail.aboutInstructor"),
    mobileLabel: t("courseDetail.aboutInstructor"),
  },
  {
    id: "faq-section",
    label: t("courseDetail.getCertificate"),
    mobileLabel: t("courseDetail.getCertificate"),
    condition: () => course.value.has_certificate,
  },
]);

// === 主要邏輯函式 ===

const initPage = async () => {
  uiState.loading = true;
  try {
    const detailRes = await msCourseAPI.getCourseDetail(courseId.value);
    const data = detailRes?.data;

    if (data?.id) {
      course.value = {
        ...course.value,
        id: data.id,
        title: data.title || "",
        en_title: data.title_en || "",
        description: data.content || "",
        en_description: data.content_en || "",
        agenda: data.agenda || "",
        en_agenda: data.agenda_en || "",
        categories: Array.isArray(data.categories) ? data.categories : [],
        tags: Array.isArray(data.tags) ? data.tags : [],
        external_url: data.external_url || "",
        is_registration_disabled: data.is_registration_disabled || false,
        instructors: Array.isArray(data.instructors) ? data.instructors : [],
        chapters: Array.isArray(data.chapters) ? data.chapters : [],
        has_certificate: false,
        has_exam: false,
        class_location: data.class_location || "",
        registration_start_at: data.registration_start_at || "",
        registration_end_at: data.registration_end_at || "",
        class_start_at: data.class_start_at || "",
        class_end_at: data.class_end_at || "",
        status_override: data.status_override || null,
      };

      isFavorite.value = data.is_favorited === true;

      if (course.value.class_start_at || course.value.class_location) {
        activeSection.value = "time-location";
      } else {
        activeSection.value = "course-intro";
      }

      document.title = `${displayLoc(course.value, "title")} - ${t(
        "courseDetail.courseDetail"
      )}`;
    } else {
      message.error(t("courseDetail.errorGettingCourseDetail"));
    }
  } catch (e) {
    console.error(e);
    message.error(t("courseDetail.errorGettingCourseDetail"));
  } finally {
    uiState.loading = false;
  }
};

// === 滾動處理 ===
const handleScroll = (() => {
  let timeout;
  return () => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => {
      const scrollY = window.scrollY;
      const navH = 60;

      if (contentWrapperRef.value) {
        uiState.showStickyNav = scrollY > contentWrapperRef.value.offsetTop;
      }

      const isDown = scrollY > (handleScroll.lastY || 0);
      uiState.showNavbar = !uiState.showStickyNav || !isDown;
      handleScroll.lastY = scrollY;

      const sections = [
        faqSectionRef,
        instructorInfoRef,
        chapterListRef,
        workshopAgendaRef,
        courseIntroRef,
        timeLocationRef,
      ].filter((refItem) => refItem.value);
      // Check if scrolled to bottom of page
      const isAtBottom =
        window.innerHeight + window.scrollY >=
        document.documentElement.scrollHeight - 50;
      if (isAtBottom && sections.length > 0) {
        activeSection.value = sections[0].value.id;
        return;
      }

      let found = false;
      for (const refItem of sections) {
        if (refItem.value && refItem.value.offsetTop - navH * 2 <= scrollY) {
          activeSection.value = refItem.value.id;
          found = true;
          break;
        }
      }
      if (!found && sections.length > 0) {
        activeSection.value = sections[sections.length - 1].value.id;
      }
    }, 10);
  };
})();

const scrollToSection = async (id) => {
  activeSection.value = id;
  await nextTick();
  const map = {
    "time-location": timeLocationRef,
    "course-intro": courseIntroRef,
    "workshop-agenda": workshopAgendaRef,
    "chapter-list": chapterListRef,
    "instructor-info": instructorInfoRef,
    "faq-section": faqSectionRef,
  };
  const target = map[id]?.value;
  if (target) {
    const offset = uiState.showStickyNav ? 120 : 60;
    window.scrollTo({ top: target.offsetTop - offset, behavior: "smooth" });
  }
};

// === 其他操作 ===
const toggleFavorite = async () => {
  if (!localStorage.getItem("ms_platform_token"))
    return router.push("/sign-in");
  favoriteLoading.value = true;
  try {
    if (isFavorite.value) {
      await msCourseAPI.removeFavorite(courseId.value);
      isFavorite.value = false;
      message.success("已取消收藏");
    } else {
      await msCourseAPI.addFavorite(courseId.value);
      isFavorite.value = true;
      message.success("課程已收藏成功");
    }
  } catch (e) {
    message.error("操作失敗，請稍後再試");
  } finally {
    favoriteLoading.value = false;
  }
};

const handleEnroll = () => {
  if (course.value.is_registration_disabled) return;
  const url = course.value.external_url;
  if (!url) return;
  window.open(url, "_blank", "noopener,noreferrer");
};

const getInstructorPhoto = (inst) =>
  inst.photo?.startsWith("http")
    ? inst.photo
    : inst.photo
    ? `https://via.placeholder.com/200x250` // Mock backend URL
    : "/default-instructor.png";
const handleImageError = (e) => (e.target.src = "/user.webp");

// Lifecycle
onMounted(() => {
  initPage();
  window.addEventListener("scroll", handleScroll);
});
onUnmounted(() => window.removeEventListener("scroll", handleScroll));
</script>

<style scoped lang="scss">
@use "@/styles/rich-content.scss" as rc;

.course-detail-page {
  font-family: "Noto Sans TC", sans-serif;
  padding-top: 70px;
  @media (max-width: 768px) {
    padding-top: 80px;
    padding-bottom: 80px;
  }
}
.navbar {
  transition: transform 0.3s ease-in-out !important;
}
.navbar.navbar-hidden {
  transform: translateY(-100%) !important;
}
.navbar.navbar-visible {
  transform: translateY(0) !important;
}
.sticky-nav {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background: #ffffff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  z-index: 999;
  border-bottom: 1px solid #e8e8e8;
  transform: translateY(-100%);
  transition: transform 0.3s ease-in-out, top 0.3s ease-in-out;
}
.sticky-nav.show {
  transform: translateY(0);
}
.sticky-nav.show.navbar-visible {
  top: 58px;
}
.sticky-nav.show.navbar-hidden {
  top: 0;
}
.sticky-nav-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}
.sticky-nav-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
}
.sticky-nav-left {
  flex: 1;
}
.sticky-course-title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #022964;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 400px;
}
.sticky-nav-right {
  flex-shrink: 0;
  margin-left: 20px;
}
.sticky-cta-button {
  background: #022964;
  border-color: #022964;
  color: #fff;
  font-weight: 500;
}
.sticky-cta-button:hover:not(:disabled) {
  background: #022964;
  border-color: #022964;
  color: #fff;
}
.sticky-cta-button:disabled {
  background: #f5f5f5 !important;
  border-color: #d9d9d9 !important;
  color: rgba(0, 0, 0, 0.25) !important;
  cursor: not-allowed !important;
}
.sticky-nav-divider {
  margin: 0;
  border: none;
  border-top: 1px solid #e8e8e8;
}
.sticky-nav-tabs {
  display: flex;
  padding: 8px 0;
  gap: 8px;
  justify-content: flex-start;
}
.sticky-nav-tab {
  width: 96px;
  height: 40px;
  text-align: center;
  padding: 8px 16px;
  text-decoration: none;
  color: #000;
  background: transparent;
  font-weight: 500;
  border-bottom: none;
  transition: all 0.3s ease;
  font-size: 14px;
  white-space: nowrap;
  border-radius: 6px;
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.sticky-nav-tab:hover {
  color: #000;
  opacity: 0.85;
  text-decoration: none;
}
.sticky-nav-tab.active {
  color: #1341ab;
  background-color: #e2f2ff;
  border-bottom-color: transparent;
  font-weight: 600;
}
.sticky-nav-tab .desktop-text {
  display: inline;
}
.sticky-nav-tab .mobile-text {
  display: none;
}
@media (max-width: 768px) {
  .sticky-nav-content {
    padding: 8px 0;
  }
  .sticky-course-title {
    font-size: 16px;
    max-width: 200px;
  }
  .sticky-nav-right {
    margin-left: 12px;
  }
  .sticky-cta-button {
    font-size: 14px;
    padding: 4px 12px;
    height: auto;
  }
  .sticky-nav-tabs {
    padding: 4px 0;
  }
  .sticky-nav-tab {
    width: auto;
    height: auto;
    padding: 6px 8px;
    font-size: 12px;
  }
  .sticky-nav-tab .desktop-text {
    display: none;
  }
  .sticky-nav-tab .mobile-text {
    display: inline;
  }
}
@media (max-width: 480px) {
  .sticky-course-title {
    font-size: 18px;
    max-width: 150px;
  }
  .sticky-cta-button {
    font-size: 16px;
    padding: 3px 10px;
  }
  .sticky-nav-tab {
    padding: 4px 6px;
    font-size: 16px;
  }
}

.course-main-title {
  font-size: 40px;
  font-weight: 700;
  color: var(--Sky-900, #1341ab);
  margin-bottom: 10px;
  line-height: 1.2;
}
.actions-bar {
  display: flex;
  gap: 12px;
  margin-top: 10px;
}
.enroll-button {
  width: 128px;
  height: 48px;
  gap: 8px;
  opacity: 1;
  padding: 12px 32px;
  border-radius: 50px;
  font-weight: 600;
  font-size: 16px;
  background: var(--Sky-700, #1171dd);
  color: #fff;
  border: none;
}
.enroll-button:hover:not(:disabled) {
  background: var(--Sky-800, #006296);
  color: #fff;
}
.enroll-button:disabled {
  background: #f5f5f5 !important;
  border-color: #d9d9d9 !important;
  color: rgba(0, 0, 0, 0.25) !important;
  cursor: not-allowed !important;
}
.content-wrapper {
  max-width: 1200px;
  margin: 20px auto;
  padding: 0 20px;
}
.benefit-icon {
  font-size: 20px;
  margin-right: 12px;
  flex-shrink: 0;
}
.instructor-link {
  color: #022964;
  cursor: pointer;
  text-decoration: none;
  transition: all 0.3s ease;
}
.instructor-link:hover {
  color: #1e40af;
  text-decoration: underline;
}
.content-card {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 20px;
  background-color: #ffffff;
}
.time-location-card {
  border: 1px solid #e9f1fe;
  background-color: #ffffff;
}
.time-row {
  font-size: 16px;
  font-weight: 500;
  color: #1f2937;
  margin-bottom: 12px;
}
.location-row {
  font-size: 16px;
  color: #4b5563;
}
:deep(.course-description) {
  @include rc.rich-content;
}
.course-description {
  color: #4b5563;
  line-height: 1.6;
  margin-bottom: 20px;
}
.course-description :deep(ul),
.course-description :deep(ol) {
  padding-left: 40px;
  margin-bottom: 1em;
  margin-top: 1em;
}
.course-description :deep(ul) {
  list-style-type: disc;
}
.course-description :deep(ol) {
  list-style-type: decimal;
}
.no-chapters,
.no-instructors {
  color: #6b7280;
  text-align: center;
  padding: 30px 0;
}
.instructors-info {
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
}
.instructor-card {
  display: flex;
  align-items: flex-start;
  padding: 24px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background-color: #ffffff;
  gap: 20px;
}
.instructor-photo {
  width: 200px;
  height: 250px;
  flex-shrink: 0;
}
.instructor-photo img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 10px;
}
.instructor-content {
  flex: 1;
  min-width: 0;
}
.instructor-header {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  margin-bottom: 10px;
}
.instructor-name {
  font-size: 18px;
  font-weight: 600;
  margin: 0;
}
.instructor-position {
  color: #6b7280;
  font-size: 14px;
  line-height: 1.5;
}
.instructor-description {
  color: #4b5563;
  font-size: 16px;
  line-height: 1.6;
  margin: 0;
  white-space: pre-wrap;
}
@media (max-width: 768px) {
  .course-main-title {
    font-size: 28px;
    font-weight: 700;
    max-width: 100%;
    word-break: break-word;
    margin-bottom: 20px;
  }
  .actions-bar {
    display: flex;
    flex-direction: row;
    width: 100%;
    gap: 8px;
  }
  .enroll-button,
  .bookmark-button {
    height: 45px;
    font-size: 14px;
    display: flex;
    justify-content: center;
    align-items: center;
  }
  .instructor-card {
    gap: 12px;
    padding: 12px;
    word-break: break-word;
  }
  .instructor-photo {
    width: 96px;
    height: 120px;
  }
  .instructor-content {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
  }
  .instructor-header {
    flex-direction: row;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
    flex-wrap: wrap;
  }
  .instructor-name,
  .instructor-position {
    font-size: 14px;
    margin: 0;
  }
  .instructor-description {
    font-size: 14px;
  }
  .content-card {
    padding: 12px;
    word-break: break-word;
  }
  .content-wrapper {
    padding: 0 15px;
    overflow-x: hidden;
    width: 100%;
    box-sizing: border-box;
  }
}

.bookmark-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 16px;
  width: 100px;
  height: 48px;
  gap: 4px;
  opacity: 1;
  padding: 12px 20px;
  border-radius: 502px;
  border: 2px solid var(--Sky-700, #1171dd);
  color: #1171dd;
  background: transparent;
  transition: all 0.3s ease;
  line-height: 1;
}
.bookmark-button :deep(.ant-btn-icon) {
  display: inline-flex;
  align-items: center;
}
.bookmark-button:hover {
  background-color: rgba(0, 139, 205, 0.05);
  border-color: var(--Sky-800, #006296);
  color: #1171dd;
}
.bookmark-button.is-favorited {
  background-color: rgba(0, 139, 205, 0.08);
}
.bookmark-icon-container {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  vertical-align: middle;
  line-height: 1;
  margin-right: -4px;
}
.bookmark-icon {
  width: 16px;
  height: 16px;
  object-fit: contain;
  display: inline-block;
  vertical-align: middle;
  margin-right: 4px;
  color: #1171dd;
}
.chapters-collapse {
  width: 100%;
  background-color: transparent;
  border: none;
}
:deep(.ant-collapse) {
  background-color: transparent;
  border: none;
  width: 100%;
}
:deep(.ant-collapse-item) {
  margin-bottom: 16px;
  background-color: #ffffff;
  overflow: hidden;
  width: 100%;
  border: 1px solid #ffffff !important;
  border-radius: 10px !important;
}
:deep(.ant-collapse-header) {
  padding: 16px 20px !important;
  font-size: 16px !important;
  font-weight: 600 !important;
  color: #000 !important;
  background-color: #e9f1fe !important;
  position: relative !important;
  display: flex !important;
  flex-direction: row-reverse !important;
  justify-content: flex-end !important;
  align-items: center !important;
}
:deep(.ant-collapse-arrow) {
  position: absolute !important;
  right: 20px !important;
  left: auto !important;
  top: 50% !important;
  transform: translateY(-50%) !important;
}
:deep(.ant-collapse-header-text) {
  flex: 1 !important;
}
:deep(.ant-collapse-content-box) {
  padding: 0 !important;
  background-color: #f8fbff;
}
.content-nav-wrapper {
  position: sticky;
  top: 0;
  background-color: #fff;
  z-index: 100;
}
.content-nav {
  display: flex;
  max-width: 1200px;
  margin: 0 auto;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  gap: 8px;
  padding: 10px 0;
}
.content-nav::-webkit-scrollbar {
  display: none;
}
.content-nav .nav-item {
  width: 96px;
  height: 40px;
  opacity: 1;
  padding: 8px 16px;
  border-radius: 6px;
  background: transparent;
  color: #000;
  font-size: 0.875rem;
  font-weight: 500;
  text-decoration: none;
  white-space: nowrap;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  border-bottom: none;
}
.content-nav .nav-item:hover {
  color: #000;
  opacity: 0.85;
}
.content-nav .nav-item.active {
  color: #1341ab;
  background-color: #e2f2ff;
  font-weight: 600;
}
.nav-item .mobile-text {
  display: none;
}
.nav-item .desktop-text {
  display: inline;
}
:deep(.light-blue-banner) {
  background: linear-gradient(269.97deg, #8dc8ff, #e2f2ff) !important;
}
.sticky-nav-tab .mobile-text {
  display: none;
}
.sticky-nav-tab .desktop-text {
  display: inline;
}
@media (max-width: 768px) {
  .nav-item .mobile-text {
    display: inline;
  }
  .nav-item .desktop-text {
    display: none;
  }
  .sticky-nav-tab .mobile-text {
    display: inline;
  }
  .sticky-nav-tab .desktop-text {
    display: none;
  }
}
.content-sections {
  max-width: 1200px;
  margin: 20px auto;
  padding: 0 8px;
}
.content-section {
  padding: 20px 0;
}
.content-section:last-child {
  border-bottom: none;
  padding-bottom: 60px;
}
.section-title {
  font-size: 22px;
  font-weight: 700;
  color: #000000;
  margin-bottom: 20px;
}
@media (max-width: 768px) {
  .content-nav .nav-item {
    width: auto;
    height: auto;
    padding: 10px 16px;
    font-size: 0.875rem;
  }
  .content-section {
    padding: 30px 0;
  }
  .section-title {
    font-size: 18px;
    margin-bottom: 16px;
  }
}
.certificate-subtitle {
  font-size: 16px;
  color: #000;
  font-weight: 400 !important;
}
.sub-chapter-item {
  display: flex;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #e9e9e9;
  transition: all 0.2s ease;
}
.sub-chapter-item:last-child {
  border-bottom: none;
}
.sub-chapter-item:hover {
  font-weight: 600;
}
.sub-chapter-icon {
  margin-right: 15px;
  color: #022964;
  font-size: 20px;
  display: flex;
  align-items: center;
  opacity: 0.8;
}
.sub-chapter-item:hover .sub-chapter-icon {
  opacity: 1;
}
.sub-chapter-content {
  flex: 1;
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.sub-chapter-title {
  font-size: 15px;
  color: #1f2937;
}
.sub-chapter-duration {
  font-size: 13px;
  color: #6b7280;
}

.detail-status-tag {
  display: inline-block;
  padding: 4px 16px;
  background-color: #ffffff;
  border-radius: 50px;
  font-family: "Noto Sans TC", sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.4;
  margin-bottom: 12px;

  &.not_started,
  &.open {
    color: #1341ab;
  }
  &.closed,
  &.ended {
    color: #6c6c6c;
  }
}

:deep(.content-sections img) {
  cursor: zoom-in;
}
</style>
